/**
 * Utility functions for consistent badge styling across the application
 */

export interface BadgeStyle {
  variant: 'default' | 'secondary' | 'destructive' | 'outline'
  className: string
}

/**
 * Get color-coded badge style for tracker types
 */
export function getTrackerBadgeStyle(trackerName: string): BadgeStyle {
  const lower = trackerName.toLowerCase()

  if (lower.includes('bug')) {
    return {
      variant: 'outline',
      className: 'border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/50'
    }
  }

  if (lower.includes('feature')) {
    return {
      variant: 'outline',
      className: 'border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/50'
    }
  }

  if (lower.includes('support')) {
    return {
      variant: 'outline',
      className: 'border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-950/50'
    }
  }

  if (lower.includes('task')) {
    return {
      variant: 'outline',
      className: 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50'
    }
  }

  // Default for unknown tracker types
  return {
    variant: 'outline',
    className: 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50'
  }
}

/**
 * Get color-coded badge style for status
 */
export function getStatusBadgeStyle(statusName: string, isClosed: boolean): BadgeStyle {
  if (isClosed) {
    return {
      variant: 'outline',
      className: 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50'
    }
  }

  const lower = statusName.toLowerCase()

  if (lower.includes('new') || lower.includes('open')) {
    return {
      variant: 'outline',
      className: 'border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/50'
    }
  }

  if (lower.includes('progress') || lower.includes('assigned') || lower.includes('active')) {
    return {
      variant: 'outline',
      className: 'border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-950/50'
    }
  }

  if (lower.includes('resolved') || lower.includes('fixed') || lower.includes('done')) {
    return {
      variant: 'outline',
      className: 'border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-950/50'
    }
  }

  if (lower.includes('feedback') || lower.includes('review')) {
    return {
      variant: 'outline',
      className: 'border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-950/50'
    }
  }

  // Default for unknown status
  return {
    variant: 'outline',
    className: 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50'
  }
}

/**
 * Get color-coded badge style for priority
 */
export function getPriorityBadgeStyle(priorityId: number, priorityName?: string): BadgeStyle {
  // High priority (4-5)
  if (priorityId >= 4) {
    return {
      variant: 'outline',
      className: 'border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/50'
    }
  }

  // Normal priority (3)
  if (priorityId === 3) {
    return {
      variant: 'outline',
      className: 'border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/50'
    }
  }

  // Low priority (1-2)
  return {
    variant: 'outline',
    className: 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50'
  }
}
